import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type AppLayoutOverride = boolean | null

type AppLayoutOverrideContextValue = {
  override: AppLayoutOverride
  setOverride: (value: AppLayoutOverride) => void
}

const noop = () => {}

const defaultContextValue: AppLayoutOverrideContextValue = {
  override: null,
  setOverride: noop,
}

const AppLayoutOverrideContext = createContext<AppLayoutOverrideContextValue>(defaultContextValue)

/**
 * Provides the product refresh preview override state to the app.
 * Mount once near the app root. The override (`null | true | false`) gates both
 * `useProductRefreshAppShell` and `useProductRefreshUiTheme`.
 */
export function AppLayoutOverrideProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideState] = useState<AppLayoutOverride>(null)
  const setOverride = useCallback((value: AppLayoutOverride) => {
    setOverrideState(value)
  }, [])
  const value = useMemo(
    () => ({ override, setOverride }),
    [override, setOverride]
  )

  return (
    <AppLayoutOverrideContext.Provider value={value}>
      {children}
    </AppLayoutOverrideContext.Provider>
  )
}

/** Shared preview gate for product refresh shell + UI theme; used only by product-refresh hooks. */
export function useProductRefreshPreviewContext(): AppLayoutOverrideContextValue {
  return useContext(AppLayoutOverrideContext)
}
